import { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, CartesianGrid,
  XAxis, YAxis, Tooltip, Cell,
} from 'recharts'
import { getManagementHours, exportManagementReport } from '../../api/client'
import { StatCardModern, PrimaryButton, Ico } from '../PageHeader'
import { currentLocale } from '../../i18n'

const PERIODS = ['last_month', 'last_quarter', 'ytd']

function monthLabel(year, month) {
  return new Date(year, month - 1, 1)
    .toLocaleDateString(currentLocale(), { month: 'short', year: 'numeric' })
}

function fmtHours(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

const BADGE = 'inline-block text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap'

// Naming the weeks beats counting them: "3 weeks over" sends someone hunting,
// "Apr 6 (64h), May 4 (71h)" tells them where to look.
function weekList(weeks) {
  return weeks
    .map(w => `${new Date(w.week_start + 'T00:00:00')
      .toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })} (${fmtHours(w.hours)}h)`)
    .join(', ')
}

// Over-max and under-min are separate signals: one means the project is
// burning more than it was budgeted, the other that it is stalling. The
// lifetime badge is judged against every hour ever logged, not the period.
function WarningBadges({ project, t }) {
  const overTotal = project.total_hour_limit != null
    && project.lifetime_hours > project.total_hour_limit
  if (!project.weeks_over && !project.weeks_under && !overTotal) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {project.weeks_over > 0 && (
        <span title={`${t('management.weeksOverTooltip')} ${weekList(project.over_weeks)}`}
              className={`${BADGE} bg-red-50 text-red-600 border-red-100`}>
          ⚠ {t('management.weeksOver', { count: project.weeks_over })}
        </span>
      )}
      {project.weeks_under > 0 && (
        <span title={`${t('management.weeksUnderTooltip')} ${weekList(project.under_weeks)}`}
              className={`${BADGE} bg-amber-50 text-amber-700 border-amber-100`}>
          ↓ {t('management.weeksUnder', { count: project.weeks_under })}
        </span>
      )}
      {overTotal && (
        <span title={t('management.overTotalTooltip', {
                hours: fmtHours(project.lifetime_hours),
                limit: fmtHours(project.total_hour_limit),
              })}
              className={`${BADGE} bg-red-100 text-red-700 border-red-200`}>
          ⛔ {t('management.overTotalBadge')}
        </span>
      )}
    </div>
  )
}

function MonthChart({ project, t }) {
  const stroke = project.color || '#4b5563'
  const data = project.months.map(m => ({
    label: monthLabel(m.year, m.month),
    hours: m.hours,
    ceiling: m.ceiling,
  }))
  // The ceiling is derived per month (rate x weeks in that month), so it steps
  // rather than sitting flat — a five-Monday month has a higher one.
  const hasCeiling = data.some(d => d.ceiling != null)
  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-100">
      <p className="text-xs font-medium text-gray-500 mb-3">{t('management.monthChartTitle')}</p>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 24, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={44} />
          {/* Two series now, so the label has to come from the series itself
              rather than being hardcoded to "Hours". */}
          <Tooltip formatter={(v, name) => [`${fmtHours(v)} h`, name]} />
          {/* A single-month period gives one point, so the dot has to carry it. */}
          <Line
            type="monotone" dataKey="hours" stroke={stroke} strokeWidth={2}
            name={t('management.columns.hours')}
            dot={{ r: 3, fill: stroke, strokeWidth: 0 }} activeDot={{ r: 5 }}
          />
          {hasCeiling && (
            <Line type="stepAfter" dataKey="ceiling" stroke="#ef4444"
                  strokeDasharray="4 3" strokeWidth={1.5} dot={false}
                  name={t('management.ceilingSeries')} connectNulls={false} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function HoursByProject() {
  const { t } = useTranslation('engineering')
  const [period, setPeriod] = useState('ytd')
  const [sCurveOnly, setSCurveOnly] = useState(false)
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setLoadError(false)
    setExpanded(null)
    getManagementHours(period, sCurveOnly)
      .then(d => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [period, sCurveOnly])

  const handleDownload = async () => {
    setDownloading(true)
    setDownloadError(false)
    try {
      await exportManagementReport(period, sCurveOnly)
    } catch {
      setDownloadError(true)
    } finally {
      setDownloading(false)
    }
  }

  const projects = data?.projects || []
  const total = data?.total_hours || 0
  // Projects, not weeks — the card is labelled "Projects over limit" and sits
  // beside two other project-level figures.
  const breachedCount = projects.filter(p => p.weeks_over > 0).length

  return (
    <div className="px-8 pb-8 pt-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">
            {t('management.title')}
          </h1>
          {data && (
            <p className="text-sm text-gray-500 mt-1">
              {t('management.range', { start: data.start, end: data.end })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={sCurveOnly}
              onChange={e => setSCurveOnly(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('management.sCurveOnly')}
          </label>
          <PrimaryButton onClick={handleDownload} disabled={downloading || !data}>
            {downloading ? t('management.downloading') : t('management.download')}
          </PrimaryButton>
        </div>
      </div>

      {downloadError && (
        <p className="text-sm text-red-600 mb-4">{t('management.downloadFailed')}</p>
      )}

      <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-lg mb-6">
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {t(`management.periods.${p}`)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCardModern
          icon={<Ico.Hours />} label={t('management.stats.totalHours')}
          value={fmtHours(total)} sub={data ? `${data.start} → ${data.end}` : ''}
        />
        <StatCardModern
          icon={<Ico.Folder />} label={t('management.stats.projects')}
          value={projects.length} sub=""
        />
        <StatCardModern
          icon={<Ico.Flag />}
          iconBg={breachedCount > 0 ? 'bg-red-50' : 'bg-gray-100'}
          iconColor={breachedCount > 0 ? 'text-red-600' : 'text-gray-600'}
          label={t('management.stats.overLimit')}
          value={breachedCount} sub=""
        />
      </div>

      {loadError ? (
        <p className="text-sm text-red-500 py-12 text-center">{t('management.loadFailed')}</p>
      ) : !data ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t('management.loading')}</p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center">{t('management.empty')}</p>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3">{t('management.columns.project')}</th>
                  <th className="px-4 py-3 text-right">{t('management.columns.hours')}</th>
                  <th className="px-4 py-3 w-56">{t('management.columns.share')}</th>
                  <th className="px-4 py-3">{t('management.columns.warnings')}</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => {
                  const share = total > 0 ? (p.total_hours / total) * 100 : 0
                  const open = expanded === p.project_id
                  return (
                    <Fragment key={p.project_id}>
                      <tr
                        onClick={() => setExpanded(open ? null : p.project_id)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: p.color }} />
                            <span className="font-medium text-gray-800">{p.name}</span>
                            {p.code && <span className="text-xs text-gray-400">{p.code}</span>}
                            {p.is_s_curve && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                                {t('management.sCurveBadge')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-800">
                          {fmtHours(p.total_hours)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full"
                                   style={{ width: `${share}%`, backgroundColor: p.color }} />
                            </div>
                            <span className="w-11 text-right tabular-nums text-xs text-gray-500">
                              {share.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <WarningBadges project={p} t={t} />
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={4} className="p-0">
                            <MonthChart project={p} t={t} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-medium text-gray-600 mb-4">{t('management.chartTitle')}</p>
            <ResponsiveContainer width="100%" height={Math.max(200, projects.length * 34)}>
              <BarChart data={projects} layout="vertical"
                        margin={{ top: 0, right: 24, bottom: 0, left: 8 }}>
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                <Tooltip formatter={v => `${fmtHours(v)} h`} />
                <Bar dataKey="total_hours" radius={[0, 4, 4, 0]}>
                  {projects.map(p => (
                    <Cell key={p.project_id}
                          fill={p.weeks_over > 0 ? '#ef4444' : p.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
